import { Link } from 'react-router-dom';
import { siteInfo, faqItems } from '@/mocks/site';
import Breadcrumb from '@/components/base/Breadcrumb';
import type { BreadcrumbItem } from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';

const breadcrumbItems: BreadcrumbItem[] = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '白丝萝莉',
    description: siteInfo.description,
    foundingDate: siteInfo.founded,
    url: 'https://baisiluoli.com',
    email: siteInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/baisiluoli',
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://baisiluoli.com${item.href}` : undefined,
    })),
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative w-full h-[240px] md:h-[300px] overflow-hidden mt-16">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Lolita%20fashion%20community%20brand%20story%2C%20elegant%20dresses%20on%20display%20in%20soft%20boutique%20setting%2C%20warm%20cream%20and%20blush%20pink%20tones%2C%20sophisticated%20feminine%20brand%20editorial%20photography%20with%20gentle%20natural%20light&width=1800&height=500&seq=about-hero&orientation=landscape"
            alt="关于白丝萝莉"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-xl md:text-3xl font-heading font-bold text-background-50 mb-2">
              关于我们
            </h1>
            <p className="text-sm text-background-50/85 max-w-md">
              探索洛丽塔时尚的无限魅力
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
              <div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {siteInfo.description}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  自{siteInfo.founded}年成立以来，白丝萝莉始终秉承<strong>「分享美、传递爱」</strong>的理念，致力于为每一位洛丽塔爱好者提供最优质的时尚内容。我们相信，洛丽塔时尚不仅仅是服装风格，更是一种精致生活的态度表达。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  无论你是刚刚接触洛丽塔的新手，还是资深的洛丽塔收藏家，白丝萝莉都愿成为你在洛丽塔时尚之路上最忠实的伙伴。
                </p>
              </div>
              <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Lolita%20fashion%20brand%20story%20with%20beautiful%20dresses%20displayed%20in%20warm%20boutique%20interior%2C%20soft%20cream%20and%20blush%20pink%20aesthetic%2C%20sophisticated%20feminine%20brand%20photography%20with%20gentle%20window%20light&width=800&height=600&seq=about-brand&orientation=portrait"
                  alt="白丝萝莉品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 md:mt-14">
              <div className="text-center p-4 rounded-lg bg-background-100 border border-background-200/70">
                <p className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-1">{siteInfo.members}</p>
                <p className="text-xs md:text-sm text-foreground-600">社区成员</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-100 border border-background-200/70">
                <p className="text-xl md:text-2xl font-heading font-bold text-accent-500 mb-1">{siteInfo.articles}</p>
                <p className="text-xs md:text-sm text-foreground-600">精选文章</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-100 border border-background-200/70">
                <p className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-1">{siteInfo.brands}</p>
                <p className="text-xs md:text-sm text-foreground-600">合作品牌</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-100 border border-background-200/70">
                <p className="text-xl md:text-2xl font-heading font-bold text-accent-500 mb-1">{new Date().getFullYear() - parseInt(siteInfo.founded)}+</p>
                <p className="text-xs md:text-sm text-foreground-600">年深耕</p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
              {siteInfo.timeline.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex items-start gap-6 mb-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="md:w-1/2"></div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 mt-1.5 z-10"></div>
                  <div className="md:w-1/2 pl-10 md:pl-0 md:px-6">
                    <span className="inline-block text-xs font-semibold text-accent-600 bg-accent-100 px-2.5 py-0.5 rounded-full mb-2">
                      {item.year}
                    </span>
                    <p className="text-sm text-foreground-700 leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              听听我们的用户怎么说
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {siteInfo.reputation.map((rep) => (
                <div
                  key={rep.id}
                  className="bg-background-100 border border-background-200/70 rounded-lg p-4 md:p-5"
                >
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: rep.rating }).map((_, i) => (
                      <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-xs text-accent-500"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3 italic">
                    &ldquo;{rep.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{rep.name}</p>
                    <p className="text-xs text-foreground-500">{rep.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <a
                  href={`mailto:${siteInfo.contact.email}`}
                  className="text-xs text-foreground-600 hover:text-primary-500 transition-colors duration-200"
                >
                  {siteInfo.contact.email}
                </a>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-600">{siteInfo.contact.wechat}</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">线下地址</h4>
                <p className="text-xs text-foreground-600">{siteInfo.contact.address}</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-100 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-4 md:px-5 py-3.5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">
                      {item.question}
                    </span>
                    <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-600"></i>
                    </span>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 md:px-5 pb-4">
                      <p className="text-sm text-foreground-700 leading-relaxed border-t border-background-200/70 pt-3">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      </main>

      <Footer />
    </>
  );
}